import React, { useEffect, useRef, useState } from 'react';
import { LogOut, Newspaper, ChevronDown, User, FileText, LockKeyholeOpen, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchAbsensiKaryawan, fetchBeritaData, fetchLaporData, fetchPersonalData } from '../../controller/mobile/home';
import { BeritaItemData, LaporItemData, DashboardCardItem } from '../../controller/mobile/types';
import { s } from '../../assets/style/home';
import { getCache } from '../../util/Cache';

// ======================== TYPES ========================

interface PersonalData {
  nama: string;
  nik: string;
  foto_karyawan?: string;
  contract_end_date?: string;
}

type RolePermissionValue = string | boolean | number | null | undefined;

interface Role {
  mobile?: Record<string, Array<Record<string, RolePermissionValue>>>;
  desktop?: Record<string, Array<Record<string, RolePermissionValue>>>;
}

const normalizeRoleData = (value: unknown): Role | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      return normalizeRoleData(JSON.parse(value));
    } catch {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value as Role;
  }

  return null;
};

// ======================== HELPERS ========================

const asBoolean = (value: RolePermissionValue): boolean => value === true || value === 'true' || value === 1 || value === '1';

const getMobilePermissions = (role: Role | null): Record<string, Array<Record<string, RolePermissionValue>>> => {
  if (!role) return {};

  const mobile = role.mobile ?? (role as Record<string, unknown>)?.mobile;
  if (mobile && typeof mobile === 'object') return mobile as Record<string, Array<Record<string, RolePermissionValue>>>;

  return {};
};

const hasMenuAccess = (role: Role | null, id: string): boolean =>
  Object.values(getMobilePermissions(role)).some(entries =>
    Array.isArray(entries) && entries.some(entry => asBoolean(entry?.[id] as RolePermissionValue))
  );

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'belum respon': return '#ef4444';
    case 'respon':       return '#3b82f6';
    case 'eksekusi':     return '#f59e0b';
    case 'selesai':      return '#22c55e';
    default:             return '#94a3b8';
  }
};

const getMenuIcon = (icon: string, size: number, color: string): React.ReactNode => {
  switch (icon) {
    case 'person':   return <User size={size} color={color} />;
    case 'document': return <FileText size={size} color={color} />;
    case 'lock': return <LockKeyholeOpen size={size} color={color} />;
    default:         return <User size={size} color={color} />;
  }
};

const LaporanItem: React.FC<LaporItemData & { showImage?: boolean }> = ({
  nama, no_pelanggan, status,
}) => {
  const displayNama = nama.length > 7 ? nama.substring(0, 7) + '...' : nama;
  const color = getStatusColor(status);
  return (
    <div style={s.cardBerita}>
      <div style={s.infoBerita}>
        <span style={s.codeBerita}>{displayNama}</span>
        <span style={s.nameBerita}>{no_pelanggan}</span>
        <div style={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: color,
          paddingInline: 4,
          paddingBlock: 2,
          borderRadius: 4,
          display: 'inline-block',
          alignSelf: 'flex-start',
          marginTop: 2,
        }}>
          <span style={{ color, fontSize: 12 }}>{status}</span>
        </div>
      </div>
    </div>
  );
};

const BeritaItem: React.FC<BeritaItemData> = ({
  title, slug, author, published_date, read_time_minutes, covert_image_url, views,
}) => {
  const displayTitle = title.length > 25 ? title.substring(0, 25) + '...' : title;
  const displayDate = new Date(published_date).toLocaleDateString('id-ID', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
  // Fix: gunakan URL gambar jika ada, tampilkan placeholder jika tidak
  const imageUrl = covert_image_url?.startsWith('http')
    ? covert_image_url
    : covert_image_url
      ? `https://pdam-dev.alanzulwas.my.id/gt/v1/storage/${covert_image_url}`
      : '';

  return (
    <a
      href={`https://web.tirtamoolango.co.id/berita/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', display: 'block' }}
    >
      <div style={s.cardBerita}>
        {imageUrl ? (
          <img src={imageUrl} alt={displayTitle} style={s.thumbBerita as React.CSSProperties} />
        ) : (
          <div style={s.thumbPlaceholder}>
            <Newspaper size={24} color="#64748b" />
          </div>
        )}
        <div style={s.infoBerita}>
          <span style={s.codeBerita}>{displayTitle}</span>
          <span style={s.nameBerita}>{author}</span>
        </div>
        <div style={s.otherInfoBerita}>
          <span style={s.otherInfoText}>{displayDate}</span>
          <span style={s.otherInfoText}>{views} views • {read_time_minutes} min</span>
        </div>
      </div>
    </a>
  );
};

// ======================== MAIN COMPONENT ========================

export default function MainMobile() {
  const navigate = useNavigate();
  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const carouselIndexRef = useRef(0);

  // ======================== DATA ========================

    const [beritaData, setBeritaData] = useState<BeritaItemData[]>([]);
    const [laporData, setLaporData] = useState<LaporItemData[]>([]);
    const [role, setRole] = useState<Role | null>(null);
    const [personalData, setPersonalData] = useState<any>(null);
    const [idKaryawan, setIdKaryawan] = useState<string>("");
    const [absensiData, setAbsensiData] = useState<any>();

  const infiniteData = [...laporData]
    .sort((a, b) => new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime())
    .slice(0, 14);

  const dashboardCardItems: DashboardCardItem[] = [
    { id : 'absensi', label: 'Absensi',  icon: 'person',   bgColor: '#6366f1', route: 'personalia' },
    { id : 'segel',   label: 'Segel',    icon: 'lock',   bgColor: '#fd8f00', route: 'segel' },
    { id : 'form_absensi', label: 'Form Absensi', icon: 'document', bgColor: '#ec4899', route: 'form-absensi' },
    // { label: 'Laporan Pengaduan', icon: 'document', bgColor: '#ec4899', route: 'lapor' },
  ];

  useEffect(() => {
    let cancelled = false;

    const loadEmployeeId = async () => {
      const currentUser = await getCache('current_user');
      if (cancelled) return;

      const roleData = currentUser?.role_data ?? currentUser?.roleData ?? currentUser?.role ?? null;
      setRole(normalizeRoleData(roleData));
      const employeeId = currentUser?.id_karyawan;
      if (employeeId !== undefined && employeeId !== null && employeeId !== '') {
        setIdKaryawan(String(employeeId));
      }
    };

    void loadEmployeeId().catch(error => {
      if (!cancelled) console.error('Failed to read current user from cache:', error);
    });
    void fetchBeritaData()
      .then(data => {
        if (!cancelled) {
          setBeritaData(data.data.filter((item: BeritaItemData) => item.publish === 'publish' && item.published_date).sort((a: any, b: any) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime()));
        }
      })
      .catch(error => {
        if (!cancelled) console.error('Failed to load dashboard news:', error);
      });
    void fetchLaporData()
      .then(data => {
        if (!cancelled) setLaporData(data.data);
      })
      .catch(error => {
        if (!cancelled) console.error('Failed to load dashboard reports:', error);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!idKaryawan) return;

    let cancelled = false;
    const loadEmployeeData = async () => {
      const [personalResult, attendanceResult] = await Promise.allSettled([
        fetchPersonalData(idKaryawan),
        fetchAbsensiKaryawan(idKaryawan),
      ]);

      if (cancelled) return;

      if (personalResult.status === 'fulfilled') {
        setPersonalData(personalResult.value.data?.[0] ?? null);
      } else {
        console.error('Error fetching personal data:', personalResult.reason);
        setPersonalData(null);
      }

      if (attendanceResult.status === 'fulfilled') {
        setAbsensiData(attendanceResult.value.data?.[0] ?? null);
      } else {
        console.error('Error fetching attendance data:', attendanceResult.reason);
        setAbsensiData(null);
      }
    };

    void loadEmployeeData();
    return () => { cancelled = true; };
  }, [idKaryawan])

  // Auto-scroll carousel (mirrors original FlatList auto-scroll logic)
  useEffect(() => {
    const total = infiniteData.length;
    if (total === 0) return;

    const interval = setInterval(() => {
      const track = carouselTrackRef.current;
      if (!track) return;

      const next = (carouselIndexRef.current + 1) % total;
      carouselIndexRef.current = next;

      track.style.transition = 'transform 0.4s ease';
      track.style.transform = `translateX(-${next * 100}%)`;

      // Reset to start after last item (seamless loop)
      if (next === total - 1) {
        setTimeout(() => {
          const t = carouselTrackRef.current;
          if (t) {
            t.style.transition = 'none';
            t.style.transform = 'translateX(0)';
            carouselIndexRef.current = 0;
          }
        }, 450);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [infiniteData.length]);

  return (
    <div style={s.page}>
      <div style={s.content}>

        {/* ── Header User ── */}
        <div style={{ ...s.headerUser, marginBottom: 0 }}>
          <div style={s.headerLeft}>
            {/* <div style={s.avatar}> */}
            <div>
              {/* <span style={s.avatarText}> */}
              <span className={`w-9 h-9 rounded-full ${s.barBg} border ${s.border} flex items-center justify-center text-[#00d4aa] font-bold text-xs`}>
                {
                  personalData?.foto_karyawan ? 
                    <><img src={process.env.REACT_APP_API_URL + "/storage/route/karyawan/" + encodeURIComponent(personalData.nama) + "/" + personalData.foto_karyawan} alt={personalData.nama} className="w-full h-full object-cover rounded-full" /></>
                      : 
                    <>{(personalData?.nama ?? '').split(' ').map((n : any) => n[0] || '').join('').slice(0, 2)}</>
                }</span>
            </div>
            <div>
              <span style={s.headerTitleUser}>{personalData?.nama || 'User'}</span>
              <span style={s.pointsText}>{personalData?.nik || '1715015052'}</span>
              <span style={s.pointsText}>{personalData?.contract_end_date ? `Kontrak hingga ${new Date(personalData.contract_end_date).toLocaleDateString()}` : 'Kontrak hingga 25 Desember 2026'}</span>
            </div>
          </div>
          <button style={s.iconButton} aria-label="Logout">
            <LogOut size={24} color="white" />
          </button>
        </div>

        {/* ── Sub Header ── */}
        <div style={{ ...s.subHeader, marginTop: 0 }}>
          {/* <span style={s.subHeaderText}>Info Absensi masih dalam pengembangan</span> */}
          <Calendar size={16} color="#64748b" />
          <div style={{ marginLeft: 8 }}>
            <span style={s.subHeaderText}>Status Absensi : {absensiData?.status != null ? absensiData?.status : "Tidak ada Status"}</span>
            {/* <span style={s.subHeaderText}>{absensiData?.status }</span> */}
            {/* <span style={s.subHeaderNote}>Diupdate dari tgl 27 Desember 2025</span> */}
          </div>
        </div>

        {/* ── Aplikasi Card ── */}
        <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Aplikasi</span>
          </div>
          <div style={s.gridCard}>
            {dashboardCardItems
              .filter(item => hasMenuAccess(role, item.id))
              .map(item => (
                <button
                  key={item.id}
                  style={s.menuItemCard}
                  onClick={() => navigate(`/mobile/${item.route}`)}
                >
                  <div style={{ ...s.iconWrapperCard, backgroundColor: item.bgColor }}>
                    {getMenuIcon(item.icon, 24, 'white')}
                  </div>
                  <span style={s.labelCard}>{item.label}</span>
                </button>
              ))}
          </div>
        </div>

        {/* ── Berita Card ── */}
        <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Berita</span>
          </div>
          <div>
            {beritaData
              .filter(item => item.publish === 'publish')
              .sort((a, b) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime())
              .slice(0, 3)
              .map((item, idx) => (
                <BeritaItem key={`v-${idx}`} {...item} />
              ))}
          </div>
          <button
            style={s.viewMoreBtn}
            onClick={() => window.open('https://web.tirtamoolango.co.id/berita', '_blank')}
          >
            <span style={s.viewMoreText}>View More</span>
            <ChevronDown size={16} color="#007AFF" />
          </button>
        </div>

        {/* ── Laporan Pengaduan Card ── */}
        {/* <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Laporan Pengaduan</span>
          </div>
          <a href="/lapor" style={s.carouselOuter}>
            <div ref={carouselTrackRef} style={s.carouselTrack}>
              {infiniteData.map((item, index) => (
                <div key={`v-${index}`} style={s.carouselItem}>
                  <LaporanItem {...item} showImage={false} />
                </div>
              ))}
            </div>
          </a>
        </div> */}

      </div>
    </div>
  );
}